import React, { useEffect, useState } from 'react'
import { useSelector } from 'react-redux';
import Warning from '../../common/alert/Warning';
import BreadCrumbBar from '../../common/breadCrumbBar/BreadCrumbBar';
import { CircularProgress, Grid, TextField, Typography } from "@mui/material";
import { ApiService } from '../../services/apiService';
import { DatePicker, LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import "dayjs/locale/ko";
import dayjs from 'dayjs';
import Table from './Table';
import MonthlyBarChart from './MonthlyBarChart';
import { formatNumber, replaceNumber } from '../../common';

function MonthlyData() {
  const authData = useSelector((state) => state.auth);
  let now = dayjs();
  let year = now.get("year");
  let month = now.get("month"); 
  const role = authData.user.role.includes("users");
  const [openWarning, setOpenWarning] = useState(false);
  const [loading, setLoading] = useState(false);
  const end =
    dayjs(`${year}-${month}`).format("YYYY-MM") +
    "-" +
    dayjs(`${year}-${month}`).daysInMonth();
  const [startDate, setStartDate] = useState(
    dayjs(`${year}-${month}`).format("YYYY-MM-DD")
  );
  const [endDate, setEndDate] = useState(end);
  const [data, setData] = useState({});
  const [chartData, setChartData] = useState({
    maxTotal: [0, 0, 0, 0, 0, 0, 0],
    avgTotal: [0, 0, 0, 0, 0, 0, 0],
    maxTrunc: [0, 0, 0, 0, 0, 0, 0],
    avgTrunc: [0, 0, 0, 0, 0, 0, 0],
  });

  function bytesToSize(bytes) {
    const gigabytes = bytes / (1024**3); // 1 GB = 1,073,741,824 bytes
    return gigabytes.toFixed(2);
  }

  const closeWarning = (data) => {
    setOpenWarning(data);
  };

  const getMonthlyDataList = async(startDate, endDate) => {
    const params = `st_day=${startDate}&ed_day=${endDate}`;
    setLoading(true);
    try {
      const monthlyData = await ApiService.getMonthlyData(params);
      const item = {...monthlyData.data};
      if (item) {
        // 이벤트 발생건수
        item.events.stats1.total_table = replaceNumber(
          item.events.stats1.total
        );
        item.events.stats1.max_table = replaceNumber(
          item?.events?.stats1?.max ?? 0
        );
        item.events.stats2.total_table = replaceNumber(
          item.events.stats2.total
        );
        item.events.stats2.max_table = replaceNumber(
          item?.events?.stats2?.max ?? 0
        );

        if (item.events.stats1.average === null) {
          item.events.stats1.average_table = 0;
          item.events.stats1.average = 0;
        } else {
          item.events.stats1.average_table = replaceNumber(
            formatNumber(item.events.stats1.average)
          );
          item.events.stats1.average = formatNumber(item.events.stats1.average);
        }

        if (item.events.stats2.average === null) {
          item.events.stats2.average_table = 0;
          item.events.stats2.average = 0;
        } else {
          item.events.stats2.average_table = replaceNumber(
            formatNumber(item.events.stats2.average)
          );
          item.events.stats2.average = formatNumber(item.events.stats2.average);
        }
        // 주행건수
        item.trips.stats1.total_table = replaceNumber(item.trips.stats1.total);
        item.trips.stats1.max_table = replaceNumber(item?.trips?.stats1?.max ?? 0);
        item.trips.stats2.total_table = replaceNumber(item.trips.stats2.total);
        item.trips.stats2.max_table = replaceNumber(item?.trips?.stats2?.max ?? 0);

        if (item.trips.stats1.average === null) {
          item.trips.stats1.average = 0;
          item.trips.stats1.average_table = 0;
        } else {
          item.trips.stats1.average_table = replaceNumber(
            formatNumber(item.trips.stats1.average)
          );
          item.trips.stats1.average = formatNumber(item.trips.stats1.average);
        }

        if (item.trips.stats2.average === null) {
          item.trips.stats2.average_table = 0;
          item.trips.stats2.average = 0;
        } else {
          item.trips.stats2.average_table = replaceNumber(
            formatNumber(item.trips.stats2.average)
          );
          item.trips.stats2.average = formatNumber(
            item.trips.stats2.average
          )
        }

        // 저장 영상 수
        item.content_count.stats1.total_table = replaceNumber(
          item.content_count.stats1.total
        );
        item.content_count.stats1.max_table = replaceNumber(
          item?.content_count?.stats1?.max ?? 0
        );
        item.content_count.stats2.total_table = replaceNumber(
          item.content_count.stats2.total
        );
        item.content_count.stats2.max_table = replaceNumber(
          item?.content_count?.stats2?.max ?? 0
        );

        if (item.content_count.stats1.average === null) {
          item.content_count.stats1.average_table = 0;
          item.content_count.stats1.average = 0;
        } else {
          item.content_count.stats1.average_table = replaceNumber(
            formatNumber(item.content_count.stats1.average)
          );
          item.content_count.stats1.average = formatNumber(
            item.content_count.stats1.average
          )
        }

        if (item.content_count.stats2.average === null) {
          item.content_count.stats2.average_table = 0;
          item.content_count.stats2.average = 0;
        } else {
          item.content_count.stats2.average_table = replaceNumber(
            formatNumber(item.content_count.stats2.average)
          );
          item.content_count.stats2.average = formatNumber(
            item.content_count.stats2.average
          );
        }

        // 저장영상 용량(byte -> GB)
        item.content_size.stats1.total_table = replaceNumber(
          bytesToSize(item.content_size.stats1.total)
        );
        item.content_size.stats1.max_table = replaceNumber(
          bytesToSize(item?.content_size?.stats1?.max ?? 0)
        );
        item.content_size.stats2.total_table = replaceNumber(
          bytesToSize(item.content_size.stats2.total)
        );
        item.content_size.stats2.max_table = replaceNumber(
          bytesToSize(item?.content_size?.stats2?.max ?? 0)
        );

        item.content_size.stats1.total = bytesToSize(
          item.content_size.stats1.total
        );
        item.content_size.stats1.max = bytesToSize(
          item?.content_size?.stats1?.max ?? 0
        );
        item.content_size.stats2.total = bytesToSize(
          item.content_size.stats2.total
        );
        item.content_size.stats2.max = bytesToSize(
          item?.content_size?.stats2?.max ?? 0
        );

        if (item.content_size.stats1.average === null) {
          item.content_size.stats1.average_table = 0;
          item.content_size.stats1.average = 0;
        } else {
          item.content_size.stats1.average_table = replaceNumber(
            bytesToSize(item.content_size.stats1.average)
          );
          item.content_size.stats1.average = bytesToSize(
            item.content_size.stats1.average
          );
        }

        if (item.content_size.stats2.average === null) {
          item.content_size.stats2.average_table = 0;
          item.content_size.stats2.average = 0;
        } else {
          item.content_size.stats2.average_table = replaceNumber(
            bytesToSize(item.content_size.stats2.average)
          );
          item.content_size.stats2.average = bytesToSize(
            item.content_size.stats2.average
          );
        }

        // 라이브 뷰 시간 (초 -> 분)
        item.liveview_time.stats1.total_table = replaceNumber(
          formatNumber(item?.liveview_time?.stats1?.total / 60)
        );
        item.liveview_time.stats1.max_table = replaceNumber(
          formatNumber(item?.liveview_time?.stats1?.max ?? 0 / 60)
        );

        item.liveview_time.stats2.total_table = replaceNumber(
          formatNumber(item?.liveview_time?.stats2?.total / 60)
        );
        item.liveview_time.stats2.max_table = replaceNumber(
          formatNumber(item?.liveview_time?.stats2?.max ?? 0 / 60)
        );

        item.liveview_time.stats1.total = formatNumber(
          item?.liveview_time?.stats1?.total / 60
        );
        item.liveview_time.stats1.max = formatNumber(
          item?.liveview_time?.stats1?.max ?? 0 / 60
        );

        item.liveview_time.stats2.total = formatNumber(
          item?.liveview_time?.stats2?.total / 60
        );
        item.liveview_time.stats2.max = formatNumber(
          item?.liveview_time?.stats2?.max ?? 0 / 60
        );

        if (item.liveview_time.stats1.average === null) {
          item.liveview_time.stats1.average_table = 0;
          item.liveview_time.stats1.average = 0;
        } else {
          item.liveview_time.stats1.average_table = replaceNumber(
            formatNumber(item.liveview_time.stats1.average / 60)
          );
          item.liveview_time.stats1.average = formatNumber(
            item.liveview_time.stats1.average / 60
          );
        }

        if (item.liveview_time.stats2.average === null) {
          item.liveview_time.stats2.average_table = 0;
          item.liveview_time.stats2.average = 0;
        } else {
          item.liveview_time.stats2.average_table = replaceNumber(
            formatNumber(item.liveview_time.stats2.average / 60)
          );
          item.liveview_time.stats2.average = formatNumber(
            item.liveview_time.stats2.average / 60
          );
        }

        // 라이브뷰 횟수
        item.liveview_count.stats1.total_table = replaceNumber(
          item.liveview_count.stats1.total
        );
        item.liveview_count.stats1.max_table = replaceNumber(
          item?.liveview_count?.stats1?.max ?? 0
        );
        item.liveview_count.stats2.total_table = replaceNumber(
          item.liveview_count.stats2.total
        );
        item.liveview_count.stats2.max_table = replaceNumber(
          item?.liveview_count?.stats2?.max ?? 0
        );

        if (item.liveview_count.stats1.average === null) {
          item.liveview_count.stats1.average_table = 0;
          item.liveview_count.stats1.average = 0;
        } else {
          item.liveview_count.stats1.average_table = replaceNumber(
            formatNumber(item.liveview_count.stats1.average)
          );

          item.liveview_count.stats1.average = formatNumber(
            item.liveview_count.stats1.average
          );
        }

        if (item.liveview_count.stats2.average === null) {
          item.liveview_count.stats2.average_table = 0;
          item.liveview_count.stats2.average = 0;
        } else {
          item.liveview_count.stats2.average_table = replaceNumber(
            formatNumber(item.liveview_count.stats2.average)
          );

          item.liveview_count.stats2.average = formatNumber(
            item.liveview_count.stats2.average
          );
        }

        // 저장영상 재생횟수
        item.cloud_play.stats1.total_table = replaceNumber(
          item.cloud_play.stats1.total
        );
        item.cloud_play.stats1.max_table = replaceNumber(
          item?.cloud_play?.stats1?.max ?? 0
        );
        item.cloud_play.stats2.total_table = replaceNumber(
          item.cloud_play.stats2.total
        );
        item.cloud_play.stats2.max_table = replaceNumber(
          item?.cloud_play?.stats2?.max ?? 0
        );

        if (item.cloud_play.stats1.average === null) {
          item.cloud_play.stats1.average_table = 0;
          item.cloud_play.stats1.average = 0;
        } else {
          item.cloud_play.stats1.average_table = replaceNumber(
            formatNumber(item.cloud_play.stats1.average)
          );

          item.cloud_play.stats1.average = formatNumber(
            item.cloud_play.stats1.average
          );
        }

        if (item.cloud_play.stats2.average === null) {
          item.cloud_play.stats2.average_table = 0;
          item.cloud_play.stats2.average = 0;
        } else {
          item.cloud_play.stats2.average_table = replaceNumber(
            formatNumber(item.cloud_play.stats2.average)
          );

          item.cloud_play.stats2.average = formatNumber(
            item.cloud_play.stats2.average
          );
        }

        setData(item);
        setChartData({
          ...chartData,
          maxTotal: [
            item.events?.stats1?.max ?? 0,
            item.trips?.stats1?.max ?? 0,
            item.content_count?.stats1?.max ?? 0,
            item.content_size?.stats1?.max ?? 0,
            item.liveview_time?.stats1?.max ?? 0,
            item.liveview_count?.stats1?.max ?? 0,
            item.cloud_play?.stats1?.max ?? 0,
          ],
          avgTotal: [
            item.events?.stats1?.average ?? 0,
            item.trips?.stats1?.average ?? 0,
            item.content_count?.stats1?.average ?? 0,
            item.content_size?.stats1?.average ?? 0,
            item.liveview_time?.stats1?.average ?? 0,
            item.liveview_count?.stats1?.average ?? 0,
            item.cloud_play?.stats1?.average ?? 0,
          ],
          maxTrunc: [
            item.events?.stats2?.max ?? 0,
            item.trips?.stats2?.max ?? 0,
            item.content_count?.stats2?.max ?? 0,
            item.content_size?.stats2?.max ?? 0,
            item.liveview_time?.stats2?.max ?? 0,
            item.liveview_count?.stats2?.max ?? 0,
            item.cloud_play?.stats2?.max ?? 0,
          ],
          avgTrunc: [
            item.events?.stats2?.average ?? 0,
            item.trips?.stats2?.average ?? 0,
            item.content_count?.stats2?.average ?? 0,
            item.content_size?.stats2?.average ?? 0,
            item.liveview_time?.stats2?.average ?? 0,
            item.liveview_count?.stats2?.average ?? 0,
            item.cloud_play.stats2?.average ?? 0,
          ],
        });
      }
    } catch (error) {
      console.log("getMonthlyDataList - getMonthlyData - error ::", error);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    getMonthlyDataList(startDate, endDate);
  }, [startDate]);
  
  return (
    <div className="containList">
      {role !== true ? (
        <Warning
          open={openWarning}
          onHideWarning={closeWarning}
          warningMsg={"해당 메뉴에 대한 권한이 없습니다!"}
        ></Warning>
      ) : (
        <>
          <BreadCrumbBar menu="월별 사용 통계" smenu="" />
          {/* <div className="containItem" style={{ marginBottom: "0px" }}> */}
          <div className="containItem">
            {/* <Grid
              container
              justifyContent={"flex-start"}
              xs={12}
              style={{ alignItems: "end" }}
              alignItems={"end"}
            >
              {menuList &&
                menuList.map((menu) => {
                  return (
                    <Grid
                      item
                      xs={2}
                      sx={{
                        borderTop: "1px solid",
                        borderRight: "1px solid",
                        borderLeft: "1px solid",
                        borderColor: "#1565c0",
                        marginRight: "8px",
                        textAlign: "center",
                        cursor: "pointer",
                        borderRadius: "3px 3px 0px 0px",
                        color: openMenu === menu ? "white" : "black",
                        padding: "2px 0px",
                        backgroundColor: openMenu === menu && "#1565c0",
                        fontWeight: 500,
                      }}
                    >
                      <div
                        onClick={() => {
                          changeMenu(menu);
                        }}
                      >
                        {menu}
                      </div>
                    </Grid>
                  );
                })}
            </Grid> */}
            <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale="ko">
              <DatePicker
                views={["year", "month"]}
                inputFormat="YYYY년 MM월"
                // minDate={dayjs("2012-03-01")}
                // maxDate={dayjs("2023-06-01")}
                minDate={dayjs("2012-01-01")}
                sx={{ marginBottom: "10px" }}
                value={startDate}
                onChange={(newValue) => {
                  setStartDate(newValue.format("YYYY-MM-DD"));
                  setEndDate(
                    newValue.format("YYYY-MM") +
                      "-" +
                      dayjs(newValue).daysInMonth()
                  );
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    helperText={null}
                    sx={{
                      "& .MuiInputBase-input": {
                        height: "6px",
                      },
                    }}
                  />
                )}
              />
            </LocalizationProvider>
            <Grid alignContent={'end'}>
              <Typography>
                조회 기간 : {startDate}~{endDate}
              </Typography>
            </Grid>
          </div>
          <div
            style={{
              display: "flex",
              justifyContent: "center",
              // border: "1px solid #9e9e9e",
              // padding: "5px",
            }}
          >
            {loading && (
              <CircularProgress
                sx={{ position: "absolute", top: "50%", left: "50%" }}
              />
            )}
            <Grid container xs={12}>
              <Grid item xs={12}>
                <Table data={data} />
              </Grid>
              <Grid item xs={12}>
                <MonthlyBarChart chartData={chartData} />
              </Grid>
            </Grid>
            {/* {openMenu === "Table" && <Table data={data} />}
            {(openMenu === "Chart") && (
              <MonthlyBarChart chartData={chartData} />
            )} */}
          </div>
        </>
      )}
    </div>
  );
}

export default MonthlyData